// De energiecheck (AI golf C): one tap in the morning. Moe: a proposal for a lighter day; the fixed things stay and
// nothing moves without Ja (B31).
import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { aiEnabled, aiLight } from "../lib/ai";
import { db } from "../lib/db";
import { sync } from "../lib/sync";
import { unplanEntries } from "../lib/tasks";
import { Button, cn } from "./ui";

const LEVELS = [
  ["moe", "Moe"],
  ["gewoon", "Gewoon"],
  ["fit", "Fit"],
] as const;

async function setEnergy(date: string, energy: "moe" | "gewoon" | "fit") {
  const plan = await db.day_plans
    .where("plan_date")
    .equals(date)
    .filter((p) => !p.deleted_at)
    .first();
  await sync.mutate("day.setEnergy", { date, energy }, plan ? [plan.id] : [], async () => {
    if (plan) await db.day_plans.update(plan.id, { energy });
  });
}

export function EnergyCheck({ date }: { date: string }) {
  const energy = useLiveQuery(
    async () =>
      (
        await db.day_plans
          .where("plan_date")
          .equals(date)
          .filter((p) => !p.deleted_at)
          .first()
      )?.energy ?? null,
    [date],
  );
  const [light, setLight] = useState<{ moveIds: string[]; advice: string } | "busy" | null>(null);

  async function choose(level: "moe" | "gewoon" | "fit") {
    await setEnergy(date, level);
    // Read at the tap itself: right after loading the screen the setting may not be read in yet.
    if (level !== "moe" || !(await aiEnabled())) return;
    setLight("busy");
    try {
      setLight(await aiLight(date));
    } catch {
      setLight(null);
      toast("Een lichtere dag voorstellen kan nu niet. Doe rustig aan.");
    }
  }

  return (
    <section aria-label="Hoe voel je je" className="mb-6">
      <p className="mb-2 text-ink-soft">Hoe voel je je vandaag?</p>
      <div role="radiogroup" aria-label="Energie" className="grid grid-cols-3 gap-2">
        {LEVELS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={energy === value}
            onClick={() => void choose(value)}
            className={cn(
              "min-h-12 rounded-xl border-2 font-semibold",
              energy === value ? "border-pine bg-pine text-paper" : "border-mist bg-surface text-ink",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {light === "busy" && <p className="mt-2 text-ink-soft">Even kijken wat er vandaag af kan.</p>}
      {light && light !== "busy" && <LighterDay proposal={light} onDone={() => setLight(null)} />}
    </section>
  );
}

function LighterDay({ proposal, onDone }: { proposal: { moveIds: string[]; advice: string }; onDone: () => void }) {
  const entries = useLiveQuery(
    async () => (await db.plan_entries.bulkGet(proposal.moveIds)).filter((e) => !!e && e.status === "planned"),
    [proposal.moveIds.join()],
  );
  return (
    <div className="mt-3 rounded-2xl border-2 border-sun bg-surface p-3">
      <p className="mb-2 flex items-start gap-2">
        <Sparkles aria-hidden className="mt-1 size-4 shrink-0 text-pine" />
        <span className="[overflow-wrap:anywhere]">{proposal.advice}</span>
      </p>
      {entries && entries.length === 0 && (
        <Button variant="secondary" onClick={onDone}>
          Oké
        </Button>
      )}
      {entries && entries.length > 0 && (
        <>
          <p className="text-sm text-ink-soft">Terug naar Te doen:</p>
          <ul className="mb-3 list-disc pl-5">
            {entries.map((e) => (
              <li key={e!.id} className="[overflow-wrap:anywhere]">
                {e!.title}
              </li>
            ))}
          </ul>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={async () => {
                await unplanEntries(
                  entries.map((e) => e!),
                  "back_to_list",
                );
                toast("Een lichtere dag. Het staat bij Te doen.");
                onDone();
              }}
            >
              Ja, lichter
            </Button>
            <Button variant="secondary" onClick={onDone}>
              Nee
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
